import { Grid, Typography, styled } from "@mui/material";
import { IAircraft, IFlight, IGroundEvent, unassignedAircraftId } from "@vmosolutions/oc-interfaces";
import moment, { Moment } from "moment";
import { useCallback, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { filteredUnassignedFlights } from "src/components/Flights/DayContextMenu";
import { IFlightChip } from "src/components/Flights/FlightChip";
import { FlightGroupChip } from "src/components/Flights/FlightGroupChip";
import { timeScales } from "src/pages/FlightTimeline/TimelineUtils";
import { updateSelectedFlight } from "src/redux/actions/flights/FlightData.Actions";
import { IRootState } from "src/redux/reducers/CombinedReducers";
import { IAircraftFlights, noAircraftSelected } from "src/redux/reducers/FlightTimelineReducer";

// Aircraft Selection Imports
import { CheckboxField } from "src/components/FormikComponents/CheckboxField";
import { SelectField } from "src/components/FormikComponents/SelectField";
import {
  applyAircraftFilter,
  convertSelectedFiltersToFilterOptions,
  sortFilteredAircraft,
} from "src/pages/FlightTimeline/FlightTimelineHeader/TimelineSortFilter";
import { setSelectedAircraft } from "src/redux/actions/flights/FlightTimeline.Actions";

interface IUnassignedFlights {
  id: string;
  path: string[];
  windowProps: {
    /** The flight display window this was opened from, whose aircraft selection is the one to assign to. */
    flightDisplayId?: string;
  };
}

const OCUnassignedFlightsWindow = styled("div")(({ theme }) => ({
  padding: "10px",
  "& .assign_to_aircraft": {
    width: 263,
    "& .input_control": {
      "& label": { width: "auto" },
    },
  },
  "& .checkboxfield": { width: 200 },
  "& .event_group": {
    position: "relative",
    marginRight: "10px",
    minWidth: 170,
    "& .day_label": {
      position: "absolute",
      backgroundColor: theme.palette.background.paper,
      width: "auto",
      margin: "0px 10px",
      padding: "6px",
    },
    "& .unassigned_events_container": {
      marginTop: 15,
      padding: 10,
      borderTop: `1px solid ${theme.typography.body2.color}`,
      borderLeft: `1px solid ${theme.typography.body2.color}`,
      "& #flightgroup": { margin: 0 }, // remove spacing between groups to maximize realistae.
    },
  },
}));

interface IFlightGroup {
  [key: string]: IFlightChip[];
}
interface IEventGroup {
  [key: string]: IGroundEvent[];
}

// Map Unassigned events into their respective days.
interface IUnassignedEventsOnDays {
  [key: string]: {
    flightGroups: IFlightGroup[];
    eventGroups: IEventGroup[];
  };
}

/**
 * Return true if 2 dates ranges overlap.
 * @param a_start - unassigned start
 * @param a_end - unassigned end
 * @param b_start - assigned start
 * @param b_end - assigned end
 * @returns boolean
 */
const dateRangeOverlaps = (a_start: number, a_end: number, b_start: number, b_end: number): boolean => {
  if (a_start <= b_start && b_start <= a_end) return true; // b starts in a
  if (a_start <= b_end && b_end <= a_end) return true; // b ends in a
  if (b_start < a_start && a_end < b_end) return true; // a in b
  return false;
};

/**
 * Function which compares unassigned flights to flights already assigned to an aircraft and returns only those that conform to requirements.
 * Checks to ensure Flights do not overlap with any already assigned as well as origin of unassigned matches destination of previous.
 * @param aircraftflightsInRange - Array of selected aircraft flights in viewable range.
 * @param unassignedflightsInRange - Array of unassigned flights in viewable range.
 */
const returnConformingFlights = (aircraftflightsInRange: IFlight[], unassignedflightsInRange: IFlight[]): IFlight[] => {
  const conformingFlights = unassignedflightsInRange?.filter((unassigned) => {
    let flightConforms = true;
    aircraftflightsInRange.forEach((assigned) => {
      const flightOverlaps = dateRangeOverlaps(
        unassigned?.scheduledOut?.valueOf(),
        unassigned?.scheduledIn?.valueOf(),
        assigned?.scheduledOut?.valueOf(),
        assigned?.scheduledIn?.valueOf()
      );
      if (flightOverlaps) {
        flightConforms = false; // Unassigned flight overlaps one more existing flights!!
      }
    });

    // If flight still conforms after overlap test, check destination=>origin match
    if (flightConforms) {
      const testFlightSequence = [...aircraftflightsInRange, unassigned].sort(
        (a, b) => a.scheduledOff?.valueOf() - b.scheduledOff?.valueOf()
      );
      const unassignedIndex = testFlightSequence.findIndex((f) => f?._id === unassigned?._id);
      if (unassignedIndex !== 0 && testFlightSequence[unassignedIndex - 1]?.destination !== unassigned?.origin) {
        flightConforms = false; // Unassigned Origin does not match previous flight's destination!!
      }
    }
    return flightConforms;
  });
  return conformingFlights;
};
/**
 * Assigned flights that have already been created but aren't assigned to aircraft.
 * @param props -
 */
export const UnassignedFlightsWindow = (props: IUnassignedFlights): JSX.Element => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [layout, setLayout] = useState<"horizontal" | "vertical">("horizontal");
  const { id, path, windowProps } = props;
  const dispatch = useDispatch();

  // const userAppSettings: IUserAppSettings = useSelector((state: IRootState) => state.authentication.appSettings);
  // const { blockInfo, flightTimelineViewScale } = userAppSettings;
  // const { utc, icao } = blockInfo;
  // const aircraftRowSetting = useSelector((state: IRootState) => state.timelineFlights.aircraftRowSetting);

  // The aircraft selection is held per window. Opened from a flight display, this window works on that display's
  // selection, so clicking an aircraft in the timeline picks it here (and the picker below selects it there). Opened
  // any other way, it keeps a selection of its own.
  const selectionWindowId = windowProps?.flightDisplayId ?? id;
  const selectedAircraftArray =
    useSelector((state: IRootState) => state.timelineFlights.selectedAircraft?.[selectionWindowId]) ??
    noAircraftSelected;
  const selectedAircraftId = selectedAircraftArray?.[0];
  const aircraftGroups = useSelector((state: IRootState) => state.structures.customerStructure.aircraftGroups);

  // AIRCRAFT FILTERING AND SORTING
  const allAircraft = useSelector((state: IRootState) => state.structures.customerStructure.aircraft);
  const { flightData, aircraftFilters, aircraftSort } = useSelector((state: IRootState) => state.timelineFlights);
  const aircraftFiltersOptions = useMemo(() => {
    return convertSelectedFiltersToFilterOptions(aircraftFilters, allAircraft, aircraftGroups);
  }, [allAircraft, aircraftGroups, aircraftFilters]);

  // Apply any actively selected filters or return all aircraft.
  const filteredAircraft = useMemo(() => {
    const filtered =
      allAircraft?.length > 0 && aircraftFiltersOptions?.length > 0
        ? applyAircraftFilter(allAircraft, aircraftFiltersOptions)
        : allAircraft;
    return aircraftSort ? sortFilteredAircraft(filtered, aircraftSort) : filtered;
  }, [allAircraft, aircraftSort, aircraftFiltersOptions]);

  const selectedAircraft = useMemo(() => {
    return filteredAircraft?.find((AC) => AC?._id === selectedAircraftId);
  }, [selectedAircraftId, filteredAircraft]);

  const userSetScale = useSelector((state: IRootState) => state.timelineFlights.userSetScale);

  // How many days are showing in the active timeline?
  const daysVisibleCount = useMemo(() => {
    if (userSetScale?.length < 2) return 0;
    const tickIntervalMillis = userSetScale[1] - userSetScale[0];
    return Math.floor(moment.duration(tickIntervalMillis, "milliseconds").asDays());
  }, [userSetScale]);

  // Unassigned Events
  const aircraftFlights: IAircraftFlights = useSelector((state: IRootState) => state.timelineFlights.flightData);
  const unassignedEvents = useSelector((state: IRootState) => state.timelineFlights.flightData[unassignedAircraftId]);
  const { flights, groundEvents } = unassignedEvents;

  const unassignedflightsInRange = useMemo(() => {
    const startEpoch = userSetScale[0];
    const endEpoch = userSetScale[1];
    return flights?.filter((flight) => {
      return flight.scheduledIn.valueOf() > startEpoch && flight.scheduledOff.valueOf() < endEpoch;
    });
  }, [userSetScale, flights]);

  // filter all unnassigned flights so that only those that can be applied to a selected aircraft are shown.
  // This filter will look at origin of closest preceeding flight to ensure it matches departure of unassigned as well as ensure that there are no overlaps.
  // TODO A planned aircraft type (optional) will eventually be added to unassigned flights, use it once added.
  const [hideNonConformingFlights, setHideNonConformingFlights] = useState<boolean>(false);

  const conformingFlights = useMemo(() => {
    if (!hideNonConformingFlights) return unassignedflightsInRange;
    // Add a buffer of -1 day to start times of existing flights so overnight flights are factored into comforming algorithm.
    const startEpoch = userSetScale[0] - timeScales.day;
    const endEpoch = userSetScale[1];
    const selectedAircraftFlights = flightData?.[selectedAircraft?._id]?.flights ?? [];
    const aircraftflightsInRange = selectedAircraftFlights?.filter((flight) => {
      return flight.scheduledIn.valueOf() > startEpoch && flight.scheduledOff.valueOf() < endEpoch;
    });
    if (aircraftflightsInRange?.length === 0) {
      // If there are no aircraft flights in range, return all unassigned as conforming.
      return unassignedflightsInRange;
    } else {
      // If there are, check the flights for any conflicts and filter unassigned to include only comforming.
      return returnConformingFlights(aircraftflightsInRange, unassignedflightsInRange);
    }
    // console.log("aircraft flights in range", aircraftflightsInRange);
    // console.log("unassigned flights in range", unassignedflightsInRange);
  }, [hideNonConformingFlights, userSetScale, unassignedflightsInRange, flightData, selectedAircraft]);

  const unassignedEventsOnDays: IUnassignedEventsOnDays = useMemo(() => {
    const object: IUnassignedEventsOnDays = {};
    const timelineViewStart = moment(userSetScale[0]);
    const timelineViewEnd = moment(userSetScale[1]);
    const currentDay = timelineViewStart.startOf("day");
    for (let i = 0; i < daysVisibleCount; i++) {
      currentDay.add(1, "day");
      const flightGroupsOnDay = filteredUnassignedFlights(conformingFlights, currentDay.toDate(), aircraftFlights);
      if (currentDay < timelineViewEnd) {
        object[currentDay?.valueOf()] = {
          flightGroups: flightGroupsOnDay ?? [],
          eventGroups: [],
        };
      }
    }
    return object;
  }, [userSetScale, daysVisibleCount, conformingFlights, aircraftFlights]);

  // Display this text if there is anything that could cause issues.
  const infoText = useMemo(() => {
    if (selectedAircraftArray?.length > 1) {
      return "You have more than one aircraft selected, the last aircraft selected will be used as the assignment aircraft.";
    } else if (selectedAircraftArray?.length === 0) {
      return "Please begin by selecting an aircraft using the timeline or the selection menu below.";
    }
    return "Click on a flight or flight group to assign it to the selected aircraft.";
  }, [selectedAircraftArray]);

  /** Handle Assignment of flight groups */
  const assignFlightGroup = useCallback(
    (flights: IFlight[]): void => {
      if (!flights?.length || !selectedAircraft) {
        return;
      }
      flights.map((flight: IFlight, index: number) => {
        const payload = { ...{ aircraft: selectedAircraft }, ...{ updateType: "ReassignAircraft" } };
        dispatch(updateSelectedFlight(flight._id, payload));
        return false;
      });
    },
    [dispatch, selectedAircraft]
  );

  /** Handle Assignment of individual flights */
  const assignIndividualFlight = useCallback(
    (flightId: string): void => {
      const flight = conformingFlights.find((flight: IFlight) => flight._id === flightId);
      if (!flight || !selectedAircraft) {
        return;
      }
      const payload = { ...{ aircraft: selectedAircraft }, ...{ updateType: "ReassignAircraft" } };
      dispatch(updateSelectedFlight(flight._id, payload));
    },
    [dispatch, selectedAircraft, conformingFlights]
  );

  useLayoutEffect(() => {
    const { width, height } = containerRef.current.getBoundingClientRect();
    const newOrientation = width > height ? "horizontal" : "vertical";
    setLayout(newOrientation);
  }, [path]);

  return (
    <OCUnassignedFlightsWindow ref={containerRef} style={{ display: "flex", width: "100%", height: "100%" }}>
      {daysVisibleCount > 7 ? (
        <Typography variant="caption" color="inherit">
          Please adjust the timeline view to show 7 or less days to activate the assignment tool.
        </Typography>
      ) : (
        <Grid container direction="column" wrap="nowrap" style={{ minWidth: 300 }}>
          <Grid item style={{ margin: "8px 8px 16px 8px" }}>
            <Typography variant="caption" color="inherit">
              {infoText}
            </Typography>
          </Grid>

          <Grid container style={{ marginBottom: 15 }}>
            <SelectField
              className="assign_to_aircraft"
              name="aircraftAssignment"
              label="Assign to Aircraft"
              maxMenuHeight={400}
              isMulti={false}
              value={selectedAircraft}
              onChange={(option: IAircraft) => {
                dispatch(setSelectedAircraft(option?._id ? [option._id as string] : [], selectionWindowId));
              }}
              options={filteredAircraft}
              getOptionLabel={(ac: IAircraft) => `${ac.registration} - ${ac.aircraftType?.title}`}
              getOptionValue={(aircraft: IAircraft) => aircraft._id}
              placeholder="Select Aircraft"
              isSearchable={true}
              isClearable={true}
            />
            <CheckboxField
              className="field checkboxfield"
              label="Hide Non-Conforming Flights"
              name="hideNonConformingFlights"
              checked={hideNonConformingFlights}
              labelPlacement="end"
              onChange={() => setHideNonConformingFlights(!hideNonConformingFlights)}
              disabled={selectedAircraftArray?.length === 0}
            />
          </Grid>

          <Grid
            container
            direction={layout === "horizontal" ? "row" : "column"}
            wrap="nowrap"
            alignItems="flex-start"
            // justifyContent="center"
          >
            {Object.entries(unassignedEventsOnDays).map(([key, value], index) => {
              const day: Moment = moment(parseInt(key));
              const flightGroupArray = Object.entries(value?.flightGroups);
              return (
                <Grid container key={`unassigned_events_day_${index + 1}`} className="event_group">
                  <Grid container className="day_label" alignItems="center">
                    <Typography variant="caption" style={{ marginRight: 5 }}>
                      {day.format("MMM/yyyy")}
                    </Typography>
                    <Typography variant="caption" color="textPrimary">
                      {day.format("DD")}
                    </Typography>
                  </Grid>

                  <Grid container className="unassigned_events_container">
                    {flightGroupArray.map(([flightGroupKey, flightGroup], groupIndex) => {
                      if (Array.isArray(flightGroup) && flightGroup.length > 0) {
                        return (
                          <FlightGroupChip
                            data={flightGroup}
                            onClickGroup={selectedAircraft ? () => assignFlightGroup(flightGroup) : null}
                            onClickChip={selectedAircraft ? assignIndividualFlight : null}
                            groupKey={flightGroupKey}
                            key={flightGroupKey}
                            buttonAlignRight={true}
                          />
                        );
                      }
                      return null;
                    })}
                  </Grid>
                </Grid>
              );
            })}
          </Grid>
        </Grid>
      )}
    </OCUnassignedFlightsWindow>
  );
};
